<script setup lang="ts">
import { computed, h, onMounted, ref } from 'vue'
import { useAccessStore } from '@/stores/access'
import { useRoute, useRouter } from 'vue-router'
import type { ColumnDef } from '@tanstack/vue-table'
import { ArrowUpDown, MoreHorizontal, Plus } from 'lucide-vue-next'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from '@/components/ui/toast'
import DialogConfirm from '@/components/dialogs/DialogConfirm.vue'
import RoleFormDialog, { type RoleFormMode } from './RoleFormDialog.vue'
import { can } from '@/composables/useCan'
import { apiErrorDetail } from '@/utils/roles'
import type { Kind, Role } from '@/utils/accessDomains'

const props = defineProps<{ kind: Kind }>()

const accessStore = useAccessStore()
const route = useRoute()
const router = useRouter()

const roles = computed<Role[]>(() => accessStore.roles(props.kind))
const domainCount = computed(() => accessStore.domains(props.kind).length)
const canCreate = computed(() => can('org_role', 'create'))
const canUpdate = computed(() => can('org_role', 'update'))
const canDelete = computed(() => can('org_role', 'delete'))

const grantedCount = (role: Role) =>
  Object.values(role.permissions).filter(actions => Object.keys(actions).length > 0).length

const editRoute = (roleId: number) => ({ name: 'OrgRoleEdit', params: { org_id: route.params.org_id, role_id: roleId } })

const openEdit = (role: Role) => router.push(editRoute(role.role_id))

const dialogOpen = ref(false)
const dialogMode = ref<RoleFormMode>('create')
const dialogRole = ref<Role | null>(null)

const openDialog = (mode: RoleFormMode, role: Role | null = null) => {
  dialogMode.value = mode
  dialogRole.value = role
  dialogOpen.value = true
}

const setDefaultJoin = async (role: Role) => {
  try {
    await accessStore.setDefaultJoin(role.role_id)
    toast({ title: `New project members now join as ${role.name}` })
  } catch (e) {
    toast({ title: apiErrorDetail(e, "Couldn't change the default role. Reload and try again."), variant: 'destructive' })
  }
}

const deleteDialog = ref<InstanceType<typeof DialogConfirm> | null>(null)
const pendingDelete = ref<Role | null>(null)
const reassign = ref<{ members: number; invites: number } | null>(null)
const reassignTo = ref<string>('')
const reassignOptions = computed(() => roles.value.filter(r => r.role_id !== pendingDelete.value?.role_id))

const askDelete = (role: Role) => {
  pendingDelete.value = role
  deleteDialog.value?.open()
}

const deleteRole = async (reassignToId?: number) => {
  const role = pendingDelete.value
  if (!role) return
  try {
    await accessStore.deleteRole({ roleId: role.role_id, reassignTo: reassignToId })
    toast({ title: `${role.name} deleted` })
    pendingDelete.value = null
    reassign.value = null
  } catch (e: any) {
    const data = e?.response?.status === 409 ? e.response.data : null
    if (data && typeof data.assigned_members === 'number' && !reassignToId) {
      reassign.value = { members: data.assigned_members, invites: data.pending_invites ?? 0 }
      reassignTo.value = ''
      return
    }
    toast({ title: apiErrorDetail(e, "Couldn't delete this role. Reload and try again."), variant: 'destructive' })
  }
}

const reassignText = computed(() => {
  if (!reassign.value) return ''
  const parts = []
  const { members, invites } = reassign.value
  if (members) parts.push(`${members} ${members === 1 ? 'member' : 'members'}`)
  if (invites) parts.push(`${invites} pending ${invites === 1 ? 'invite' : 'invites'}`)
  const verb = members + invites === 1 ? 'has' : 'have'
  return `${parts.join(' and ')} ${verb} this role. Choose the role they move to.`
})

const closeReassign = (open: boolean) => {
  if (open) return
  reassign.value = null
  pendingDelete.value = null
}

onMounted(() => {
  if (route.query.new === '1') {
    if (canCreate.value) openDialog('create')
    const { new: _, ...query } = route.query
    router.replace({ query })
  }
})

const sortableHeader = (label: string) => ({ column }: any) =>
  h(Button, {
    variant: 'ghost',
    size: 'sm',
    onClick: () => column.toggleSorting(column.getIsSorted() === 'asc'),
  }, () => [label, h(ArrowUpDown, { class: 'ml-1 h-3 w-3' })])

const menuItems = (role: Role) => {
  const items = [h(DropdownMenuItem, { onClick: () => openEdit(role) }, () => (canUpdate.value ? 'Edit' : 'View'))]
  if (canUpdate.value) items.push(h(DropdownMenuItem, { onClick: () => openDialog('rename', role) }, () => 'Rename'))
  if (canCreate.value) items.push(h(DropdownMenuItem, { onClick: () => openDialog('duplicate', role) }, () => 'Duplicate'))
  if (canUpdate.value && role.kind === 'project' && !role.is_default_join) {
    items.push(h(DropdownMenuItem, { onClick: () => setDefaultJoin(role) }, () => 'Make default for joining'))
  }
  if (canDelete.value) {
    items.push(h(DropdownMenuSeparator))
    items.push(h(DropdownMenuItem, {
      class: 'text-red-700',
      disabled: !!role.template_key,
      title: role.template_key ? 'Default roles can be reset but not deleted' : undefined,
      onClick: () => askDelete(role),
    }, () => 'Delete'))
  }
  return items
}

const columns = computed<ColumnDef<Role>[]>(() => [
  {
    accessorKey: 'name',
    header: sortableHeader('Name'),
    cell: ({ row }) => {
      const role = row.original
      return h('div', { class: 'flex items-center gap-2 font-medium' }, [
        role.name,
        role.is_default_join
          ? h(Badge, { variant: 'secondary', class: 'font-normal', title: 'Members who join a project get this role' }, () => 'Default')
          : null,
      ])
    },
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => h('div', { class: 'text-muted-foreground' }, row.original.description ?? ''),
  },
  {
    id: 'permissions',
    header: 'Permissions',
    cell: ({ row }) =>
      h('div', { class: 'text-muted-foreground whitespace-nowrap' }, [
        `${grantedCount(row.original)} of ${domainCount.value} areas`,
        row.original.template_key && row.original.is_customized ? ' · customized' : '',
      ].join('')),
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => {
      const role = row.original
      return h('div', { class: 'flex justify-end', onClick: (e: MouseEvent) => e.stopPropagation() }, h(DropdownMenu, {}, () => [
        h(DropdownMenuTrigger, { asChild: true }, () =>
          h(Button, { variant: 'ghost', class: 'w-8 h-8 p-0' }, () => [
            h('span', { class: 'sr-only' }, 'Open menu'),
            h(MoreHorizontal, { class: 'w-4 h-4' }),
          ])
        ),
        h(DropdownMenuContent, { align: 'end' }, () => [
          h(DropdownMenuLabel, {}, () => 'Actions'),
          ...menuItems(role),
        ]),
      ]))
    },
  },
])
</script>

<template>
  <div>
    <div class="mb-1 flex items-center">
      <div class="basis-1/2">
        <h3 class="text-base font-bold">Roles ({{ roles.length }})</h3>
      </div>
      <div class="basis-1/2 flex justify-end">
        <Button v-if="canCreate" variant="ghost" size="sm" class="h-8" @click="openDialog('create')">
          <Plus class="w-4 h-4 mr-1" />
          New role
        </Button>
      </div>
    </div>

    <DataTable :columns="columns" :data="roles" @row-click="openEdit" />

    <RoleFormDialog
      v-model:open="dialogOpen"
      :mode="dialogMode"
      :kind="kind"
      :role="dialogRole"
      @saved="(id) => dialogMode !== 'rename' && router.push(editRoute(id))"
    />

    <DialogConfirm
      ref="deleteDialog"
      :title="`Delete ${pendingDelete?.name ?? 'role'}?`"
      description="This can't be undone."
      confirm-text="Delete"
      variant="error"
      @confirm="deleteRole()"
      @cancel="pendingDelete = null"
    />

    <Dialog :open="!!reassign" @update:open="closeReassign">
      <DialogContent class="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Delete {{ pendingDelete?.name }}?</DialogTitle>
          <DialogDescription>{{ reassignText }}</DialogDescription>
        </DialogHeader>
        <Select v-model="reassignTo">
          <SelectTrigger aria-label="Move to role">
            <SelectValue placeholder="Choose a role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem v-for="option in reassignOptions" :key="option.role_id" :value="String(option.role_id)">
              {{ option.name }}
            </SelectItem>
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="ghost" @click="closeReassign(false)">Cancel</Button>
          <Button variant="destructive" :disabled="!reassignTo" @click="deleteRole(Number(reassignTo))">
            Move and delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>
